import { useEffect, useMemo, useRef, useState } from "react";
import type { CodeElement, CodeParam } from "../../../engine/src/types.ts";
import { t } from "../lib/i18n.ts";
import { updateElement } from "../lib/ops.ts";
import type { Project } from "../../../engine/src/types.ts";
import type { EditorStore } from "../lib/store.ts";
import { useStore } from "./hooks.ts";

export const codeElementOf = (p: Project): CodeElement | undefined => Object.values(p.elements).find((e) => e.type === "code") as CodeElement | undefined;
export const isCodeProject = (p: Project | null | undefined): boolean => !!p && !!codeElementOf(p);

/** Live values of the params, as the document expects them (`?p=`): unsaved edits reach the preview without waiting for autosave. */
const overridesOf = (el: CodeElement): Record<string, string | number> => Object.fromEntries(el.params.map((q: CodeParam) => [q.key, q.value]));

/** The picture of a code project: the composition runs in a sandboxed iframe (no same-origin, see docs/CODE_COMPOSITIONS.md) and
 *  is driven only by `seek` messages, so playback, scrubbing and export all show the same pixels. */
export function CodeStage({ store }: { store: EditorStore }) {
  const st = useStore(store);
  const p = st.project!, el = codeElementOf(p)!;
  const wrap = useRef(null as HTMLDivElement | null), frame = useRef(null as HTMLIFrameElement | null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null as string | null);
  const timeRef = useRef(st.timeMs);
  timeRef.current = st.timeMs;

  // the document is reloaded (debounced) when params change, and when the server copy changes (after a save: new source from the assistant)
  const params = JSON.stringify(overridesOf(el));
  const [src, setSrc] = useState("");
  useEffect(() => {
    const h = setTimeout(() => { setReady(false); setError(null); setSrc(`/api/projects/${p.id}/code-frame?p=${encodeURIComponent(params)}&r=${st.serverRevision}`); }, src ? 250 : 0);
    return () => clearTimeout(h);
  }, [p.id, params, st.serverRevision, el.source]);

  useEffect(() => {
    const node = wrap.current!, ro = new ResizeObserver(() => setBox({ w: node.clientWidth, h: node.clientHeight }));
    ro.observe(node); setBox({ w: node.clientWidth, h: node.clientHeight });
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const w = frame.current?.contentWindow;
      if (!w || e.source !== w) return;
      const d = e.data;
      if (!d || d.modila !== 1) return;
      if (d.type === "ready") { setReady(true); setError(null); w.postMessage({ modila: 1, type: "seek", t: timeRef.current }, "*"); }
      else if (d.type === "error") setError(String(d.message ?? "error"));
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  useEffect(() => {
    if (ready) frame.current?.contentWindow?.postMessage({ modila: 1, type: "seek", t: Math.max(0, Math.min(p.duration_ms - 1, st.timeMs)) }, "*");
  }, [st.timeMs, ready]);

  const pad = 28, aw = Math.max(50, box.w - pad * 2), ah = Math.max(50, box.h - pad * 2);
  const scale = Math.min(aw / p.width, ah / p.height), fw = Math.floor(p.width * scale), fh = Math.floor(p.height * scale);
  return (
    <div className="stage" ref={wrap} aria-label={t("code.stage")}>
      <div className="frame" style={{ width: fw, height: fh, overflow: "hidden", background: "#000" }}>
        {src && <iframe ref={frame} title={t("code.stage")} src={src} sandbox="allow-scripts" referrerPolicy="no-referrer" tabIndex={-1}
          style={{ width: p.width, height: p.height, border: 0, transformOrigin: "0 0", transform: `scale(${scale})`, pointerEvents: "none", display: "block" }} />}
      </div>
      {error && <div className="stagehint" role="alert" style={{ color: "var(--danger, #d33)", maxWidth: "90%" }}>{error}</div>}
    </div>
  );
}

/** The editable side of a code project: its params, plus the (read-only) source for anyone who wants to look. */
export function CodeParamsPanel({ store }: { store: EditorStore }) {
  const st = useStore(store), p = st.project!, el = codeElementOf(p)!;
  const [showCode, setShowCode] = useState(false);
  const set = (key: string, value: string | number) => store.edit((q) => {
    const cur = codeElementOf(q)!;
    return updateElement(q, cur.id, { params: cur.params.map((x: CodeParam) => x.key === key ? { ...x, value } : x) });
  }, `cp:${key}`);
  const lines = useMemo(() => el.source.split("\n").length, [el.source]);
  return (
    <div className="stack">
      <div><h3 className="h">{t("code.params")}</h3>
        {el.params.length === 0 && <p className="muted">{t("code.no_params")}</p>}
        <div className="fields">
          {el.params.map((q: CodeParam) => {
            const id = `cp-${q.key}`, label = q.label || q.key;
            if (q.kind === "color") return <label className="f" htmlFor={id} key={q.key}>{label}<input id={id} type="color" className="input" value={String(q.value)} onChange={(e: any) => set(q.key, e.target.value.toUpperCase())} /></label>;
            if (q.kind === "number") return <label className="f" htmlFor={id} key={q.key}>{label}<input id={id} type="number" className="input" min={q.min ?? undefined} max={q.max ?? undefined} step={q.step ?? "any"}
              value={Number(q.value)} onChange={(e: any) => { const v = parseFloat(e.target.value); if (Number.isFinite(v)) set(q.key, Math.min(q.max ?? v, Math.max(q.min ?? v, v))); }} /></label>;
            const long = String(q.value).length > 40 || String(q.value).includes("\n");
            return <label className="f wide" htmlFor={id} key={q.key}>{label}{long
              ? <textarea id={id} className="textarea" style={{ minHeight: 56 }} maxLength={280} value={String(q.value)} onChange={(e: any) => set(q.key, e.target.value)} />
              : <input id={id} className="input" maxLength={280} value={String(q.value)} onChange={(e: any) => set(q.key, e.target.value)} />}</label>;
          })}
        </div>
      </div>
      <div><button className="btn sm" aria-expanded={showCode} onClick={() => setShowCode(!showCode)}>{t("code.view_source")} · {lines}</button>
        {showCode && <pre className="mono" style={{ fontSize: 11, maxHeight: 320, overflow: "auto", whiteSpace: "pre", padding: 8, border: "1px solid var(--border)", borderRadius: 6 }} tabIndex={0}>{el.source}</pre>}
        <p className="muted" style={{ fontSize: 11 }}>{t("code.hint")}</p></div>
    </div>
  );
}
